import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  RefreshCw,
  X,
  BookOpen,
  FileClock,
  LifeBuoy,
  Mail,
} from 'lucide-react';
import { AppPanelHeader, AppPanelBody } from '@vnetwork/ui/layout';
import { IconButton, Button, Spinner } from '@vnetwork/ui/primitives';
import {
  DOCS_BASE,
  docsPathForRoute,
  fetchDocArticle,
  type DocArticle,
} from './docs-content';
import { brand } from '@/config/brand';
import styles from './DocsPanel.module.css';

type Status = 'loading' | 'ready' | 'error';

/**
 * Documentation panel — docked beside the current page (same AppPanel slot the
 * assistant used), so opening docs keeps the working screen in place. Content is
 * fetched from docs.vnetwork.dev and rendered natively (see docs-content.ts).
 */
const SUPPORT_EMAIL = brand.emails.support;

export function DocsPanel({ onClose }: { onClose: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [path, setPath] = useState(() => docsPathForRoute(pathname));
  const [history, setHistory] = useState<string[]>([]);
  const [article, setArticle] = useState<DocArticle | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const bodyRef = useRef<HTMLDivElement>(null);

  // Bumped to force a re-fetch of the same path (the Reload / Try again action).
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  // Follow app navigation: when the underlying route changes while the panel
  // stays open (user switches tab/page), re-point docs at the new route's page
  // and reset the in-panel link history. The initial route is handled by the
  // `path` initializer, so skip the first render.
  const lastPathnameRef = useRef(pathname);
  useEffect(() => {
    if (pathname === lastPathnameRef.current) return;
    lastPathnameRef.current = pathname;
    setPath(docsPathForRoute(pathname));
    setHistory([]);
  }, [pathname]);

  useEffect(() => {
    let alive = true;
    setStatus('loading');
    fetchDocArticle(path)
      .then((a) => {
        if (!alive) return;
        setArticle(a);
        setStatus('ready');
      })
      .catch(() => alive && setStatus('error'));
    return () => {
      alive = false;
    };
  }, [path, reloadKey]);

  // Scroll to top (or to the in-page anchor) whenever a new page renders.
  useEffect(() => {
    if (status !== 'ready') return;
    const hash = path.includes('#') ? path.slice(path.indexOf('#') + 1) : '';
    const target = hash ? bodyRef.current?.querySelector(`#${CSS.escape(hash)}`) : null;
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ block: 'start' });
    } else {
      bodyRef.current?.scrollTo({ top: 0 });
    }
  }, [status, article, path]);

  const goTo = (next: string) => {
    if (next === path) return;
    setHistory((h) => [...h, path]);
    setPath(next);
  };

  const goBack = () => {
    setHistory((h) => {
      if (h.length === 0) return h;
      setPath(h[h.length - 1]);
      return h.slice(0, -1);
    });
  };

  // Intercept internal doc links inside the rendered article.
  const onBodyClick = (e: React.MouseEvent) => {
    const anchor = (e.target as HTMLElement).closest('a[data-docs-path]');
    if (!anchor) return;
    e.preventDefault();
    goTo(anchor.getAttribute('data-docs-path')!);
  };

  const externalHref = DOCS_BASE + (path.split('#')[0] || '');

  return (
    <>
      <AppPanelHeader
        actions={
          <>
            <IconButton
              aria-label="Open in docs site"
              variant="ghost"
              size="sm"
              asChild
            >
              <a href={externalHref} target="_blank" rel="noreferrer">
                <ExternalLink size={15} />
              </a>
            </IconButton>
            <IconButton
              aria-label="Reload"
              variant="ghost"
              size="sm"
              onClick={reload}
            >
              <RefreshCw size={15} />
            </IconButton>
            <IconButton aria-label="Close documentation" variant="ghost" size="sm" onClick={onClose}>
              <X size={16} />
            </IconButton>
          </>
        }
      >
        <span className={styles.headerTitle}>
          {history.length > 0 ? (
            <IconButton aria-label="Back" variant="ghost" size="sm" onClick={goBack}>
              <ArrowLeft size={16} />
            </IconButton>
          ) : (
            <BookOpen size={16} color="var(--vn-fg-muted)" />
          )}
          <span className={styles.headerLabel}>
            {status === 'ready' ? article?.title : 'Documentation'}
          </span>
        </span>
      </AppPanelHeader>

      <AppPanelBody>
        {status === 'loading' ? (
          <div className={styles.center}>
            <Spinner />
          </div>
        ) : status === 'error' ? (
          // A missing docs page returns 404 (guide not written yet), so we frame
          // this as "being updated" and route the user to a human instead.
          <div className={styles.empty}>
            <span className={styles.emptyIcon}>
              <FileClock size={26} />
            </span>
            <h3 className={styles.emptyTitle}>This guide is being updated</h3>
            <p className={styles.errorText}>
              We're still writing documentation for this feature. In the meantime, our
              support team can help you directly.
            </p>
            <div className={styles.emptyActions}>
              <Button
                variant="primary"
                size="sm"
                startIcon={<LifeBuoy size={14} />}
                onClick={() => {
                  navigate('/support');
                  onClose();
                }}
              >
                Create a support ticket
              </Button>
              <Button variant="secondary" size="sm" startIcon={<Mail size={14} />} asChild>
                <a href={`mailto:${SUPPORT_EMAIL}`}>Email support</a>
              </Button>
            </div>
            <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.emptyEmail}>
              {SUPPORT_EMAIL}
            </a>
            <button type="button" className={styles.retryLink} onClick={reload}>
              <RefreshCw size={12} /> Try again
            </button>
          </div>
        ) : (
          <div
            ref={bodyRef}
            className={styles.prose}
            onClick={onBodyClick}
            dangerouslySetInnerHTML={{ __html: article?.html ?? '' }}
          />
        )}
      </AppPanelBody>
    </>
  );
}
